/**
 * The only place a data source is chosen.
 *
 * The mock branch is guarded by a build-time constant: in a production build
 * (VITE_ENABLE_TEST_ROUTES=false) the condition folds to `false`, Rollup drops
 * the dynamic import, and the ~9k-line mock engine is never emitted.
 * `scripts/check-bundle.mjs` fails CI if a `mock-engine` chunk appears in dist.
 */
import type { DataSource, SourceId } from '@/data-sources/contract';
import { SourceUnavailableError } from '@/lib/errors';

const loaders: Record<SourceId, () => Promise<DataSource>> = {
  live: () => import('./live').then((m) => m.liveSource),
  mock:
    import.meta.env.VITE_ENABLE_TEST_ROUTES === 'true'
      ? () => import('./mock').then((m) => m.mockSource)
      : () => Promise.reject(new SourceUnavailableError('mock')),
};

const loaded = new Map<SourceId, Promise<DataSource>>();

export function loadSource(id: SourceId): Promise<DataSource> {
  let source = loaded.get(id);
  if (!source) {
    source = loaders[id]();
    // A failed chunk load must not be cached forever.
    source.catch(() => loaded.delete(id));
    loaded.set(id, source);
  }
  return source;
}

export const testRoutesEnabled = import.meta.env.VITE_ENABLE_TEST_ROUTES === 'true';
